import { cn } from '@/lib/utils'
import type { Content, Platform, ContentStatus } from '@/types'

export const PLATFORM_COLORS: Record<string, string> = {
  INSTAGRAM: '#E4405F',
  FACEBOOK: '#1877F2',
  TIKTOK: '#000000',
  LINKEDIN: '#0A66C2',
  TWITTER: '#1DA1F2',
  YOUTUBE: '#FF0000',
  PINTEREST: '#BD081C',
}

export const PLATFORM_LABEL: Record<Platform, string> = {
  INSTAGRAM: 'IG',
  FACEBOOK: 'FB',
  TIKTOK: 'TT',
  LINKEDIN: 'LI',
  TWITTER: 'TW',
  YOUTUBE: 'YT',
  PINTEREST: 'PT',
}

export const CONTENT_TYPE_LABEL: Record<string, string> = {
  POST: 'Post',
  REEL: 'Reel',
  STORY: 'Story',
  CAROUSEL: 'Carosello',
  VIDEO: 'Video',
  ARTICLE: 'Articolo',
  INFOGRAPHIC: 'Infografica',
}

export const STATUS_LABEL: Record<ContentStatus, string> = {
  IDEA: 'Idea',
  TODO: 'Da fare',
  IN_PRODUCTION: 'Produzione',
  READY: 'Pronto',
  IN_REVIEW: 'Review',
  CHANGES_REQUESTED: 'Modifiche',
  APPROVED: 'Approvato',
  CLIENT_APPROVED: 'Approvato cliente',
  SCHEDULED: 'Programmato',
  PUBLISHED: 'Pubblicato',
}

export const STATUS_DOT_COLOR: Record<ContentStatus, string> = {
  IDEA: 'bg-slate-400',
  TODO: 'bg-primary-300',
  IN_PRODUCTION: 'bg-warning-400',
  READY: 'bg-success-200',
  IN_REVIEW: 'bg-warning-500',
  CHANGES_REQUESTED: 'bg-danger-500',
  APPROVED: 'bg-success-500',
  CLIENT_APPROVED: 'bg-success-600',
  SCHEDULED: 'bg-accent-400',
  PUBLISHED: 'bg-success-500',
}

interface ContentCardProps {
  content: Content
  onClick?: () => void
  compact?: boolean
  dragging?: boolean
}

export function ContentCard({ content, onClick, compact = false, dragging = false }: ContentCardProps) {
  // Support both legacy platform and new platforms array
  const platforms = content.platforms ?? ((content as any).platform ? [(content as any).platform] : [])
  const primaryPlatform = platforms[0]
  const platformColor = (primaryPlatform ? PLATFORM_COLORS[primaryPlatform] : undefined) ?? '#64748B'
  const scheduledTime = content.scheduledAt
    ? new Date(content.scheduledAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    : null

  if (compact) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'w-full text-left flex items-center gap-1.5 px-1 py-[3px] rounded-md',
          'hover:bg-slate-50 transition-colors cursor-pointer',
          dragging && 'opacity-50 rotate-1 scale-105',
        )}
      >
        <div className={cn('w-[5px] h-[5px] rounded-full flex-shrink-0', STATUS_DOT_COLOR[content.status])} />
        <span className="text-[11px] text-slate-600 truncate flex-1">
          {content.title}
        </span>
      </button>
    )
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-2xl border border-slate-200 bg-white',
        'border-l-4 shadow-card hover:border-slate-300 transition-colors p-3',
        dragging && 'opacity-50 rotate-1 scale-105',
        'cursor-pointer'
      )}
      style={{ borderLeftColor: platformColor }}
    >
      <div className="flex items-start gap-2">
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 text-white mt-0.5"
          style={{ backgroundColor: platformColor }}
        >
          {platforms.map(p => PLATFORM_LABEL[p]).join(', ')}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-800 truncate leading-tight">
            {content.title}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              {CONTENT_TYPE_LABEL[content.type] ?? content.type}
            </span>
            {content.status === 'CLIENT_APPROVED' ? (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-success-600 bg-success-50 px-1.5 py-0.5 rounded">
                ✓ Cliente
              </span>
            ) : (
              <div className="flex items-center gap-1">
                <div className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', STATUS_DOT_COLOR[content.status])} />
                <span className="text-[10px] text-slate-500">{STATUS_LABEL[content.status]}</span>
              </div>
            )}
          </div>
          {scheduledTime && (
            <p className="text-[10px] text-slate-400 mt-0.5 tabular-nums">{scheduledTime}</p>
          )}
        </div>
      </div>
    </button>
  )
}
